import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  AcademicCapIcon, 
  BookOpenIcon,
  ArrowDownTrayIcon,
  CheckBadgeIcon 
} from '@heroicons/react/24/solid';
import api from '../services/api';

export default function MyCertificates() {
  const [courseCertificates, setCourseCertificates] = useState([]);
  const [moduleCertificates, setModuleCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    try {
      setLoading(true);
      const [courseResp, moduleResp] = await Promise.all([
        api.get('/certificates'),
        api.get('/module-certificates'),
      ]);
      
      setCourseCertificates(courseResp.data.certificates || []);
      setModuleCertificates(moduleResp.data.certificates || []);
    } catch (error) {
      console.error('Failed to fetch certificates:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalCertificates = courseCertificates.length + moduleCertificates.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cte-blue"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-2">
          <AcademicCapIcon className="h-10 w-10 text-yellow-500 mr-3" />
          <h1 className="text-3xl font-bold text-cte-black">My Certificates</h1>
        </div>
        <p className="text-gray-600">
          {totalCertificates === 0 
            ? "Complete modules and courses to earn certificates" 
            : `You've earned ${totalCertificates} ${totalCertificates === 1 ? 'certificate' : 'certificates'}! 🎉`
          }
        </p>
      </div>

      {totalCertificates === 0 ? (
        <div className="text-center py-16 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl border-2 border-dashed border-yellow-300">
          <AcademicCapIcon className="h-20 w-20 text-yellow-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            No Certificates Yet
          </h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Complete modules and the full course to earn certificates that showcase your achievements.
          </p>
          <Link 
            to="/dashboard" 
            className="inline-flex items-center px-6 py-3 bg-cte-blue text-white rounded-lg hover:bg-cte-blue-dark transition-colors font-semibold"
          >
            Start Learning →
          </Link>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Course Certificates */}
          {courseCertificates.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <CheckBadgeIcon className="h-7 w-7 text-green-600 mr-2" />
                Course Completion
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {courseCertificates.map((cert) => (
                  <div
                    key={cert.id}
                    className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 hover:shadow-lg transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center">
                        <div className="bg-green-100 p-3 rounded-lg">
                          <AcademicCapIcon className="h-8 w-8 text-green-600" />
                        </div>
                        <div className="ml-4">
                          <h3 className="font-bold text-gray-900 text-lg">
                            Course Completion Certificate
                          </h3>
                          <p className="text-sm text-gray-600">
                            Certificate #{cert.certificate_number}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-sm text-gray-700 mb-4">
                      <p><strong>Issued:</strong> {new Date(cert.issued_at).toLocaleDateString()}</p>
                      <p><strong>Modules Completed:</strong> {cert.completed_modules?.length || 0}</p>
                    </div>

                    <div className="flex gap-2">
                      <Link
                        to={`/certificates/${cert.id}`}
                        className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold text-sm"
                      >
                        View Certificate
                      </Link>
                      <a
                        href={`${import.meta.env.VITE_API_BASE_URL}/certificates/${cert.id}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 bg-white border-2 border-green-600 text-green-600 rounded-lg hover:bg-green-50 transition-colors font-semibold text-sm"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                        Download
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Module Certificates */}
          {moduleCertificates.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <BookOpenIcon className="h-7 w-7 text-blue-600 mr-2" />
                Module Certificates ({moduleCertificates.length})
              </h2>
              <div className="grid md:grid-cols-3 gap-4">
                {moduleCertificates.map((cert) => (
                  <div
                    key={cert.id}
                    className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-5 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start mb-3">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <BookOpenIcon className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="ml-3 flex-1">
                        <span className="text-xs font-semibold text-blue-600 uppercase block">
                          {cert.module_code}
                        </span>
                        <h3 className="font-bold text-gray-900 text-sm mt-1 line-clamp-2">
                          {cert.module_title}
                        </h3>
                      </div>
                    </div>
                    
                    <div className="text-xs text-gray-600 mb-3">
                      <p className="font-mono">{cert.certificate_number}</p>
                      <p className="mt-1">
                        {new Date(cert.issued_at).toLocaleDateString()}
                      </p>
                    </div>

                    <Link
                      to={`/module-certificates/${cert.id}`}
                      className="block w-full text-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-sm"
                    >
                      View Certificate
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
